
import { Difficulty, TestType, CheckerResult, Language, StudyLesson, AdaptiveContext, TestQuestion, TRANSLATIONS, VisualComponentType } from "../types";

let genAiClientPromise: Promise<{ ai: any; Type: any }> | null = null;
const modelName = 'gemini-3-flash-preview';

const getGenAiClient = async () => {
  if (!genAiClientPromise) {
    genAiClientPromise = (async () => {
      const mod = await import("@google/genai");
      const ai = new mod.GoogleGenAI({ apiKey: process.env.API_KEY });
      return { ai, Type: mod.Type };
    })();
  }
  return genAiClientPromise;
};

// ============================================================================
// CONSTANTES CENTRALIZADAS
// ============================================================================
const LANGUAGE_NAMES: Record<Language, string> = {
  es: 'Español',
  en: 'English',
};

const TEST_TYPE_LABELS: Record<Language, Record<TestType, string>> = {
  es: {
    [TestType.RANDOM]: 'Aleatorio',
    [TestType.ERRORS]: 'Detectar errores',
    [TestType.VOICEOVER]: 'Lectura VoiceOver',
    [TestType.DOCUMENTATION]: 'Documentación completa',
    [TestType.FOCUS_ORDER]: 'Orden de foco',
    [TestType.HEADINGS]: 'Jerarquía (Headings)',
    [TestType.REFLOW]: 'Reflow y zoom',
    [TestType.STATES]: 'Estados y errores',
    [TestType.GROUPING]: 'Elementos agrupados',
    [TestType.MODALS]: 'Modales y overlays',
  },
  en: {
    [TestType.RANDOM]: 'Random',
    [TestType.ERRORS]: 'Detect errors',
    [TestType.VOICEOVER]: 'VoiceOver reading',
    [TestType.DOCUMENTATION]: 'Complete documentation',
    [TestType.FOCUS_ORDER]: 'Focus order',
    [TestType.HEADINGS]: 'Hierarchy (Headings)',
    [TestType.REFLOW]: 'Reflow & Zoom',
    [TestType.STATES]: 'States and errors',
    [TestType.GROUPING]: 'Grouped elements',
    [TestType.MODALS]: 'Modals & overlays',
  },
};

const DIFFICULTY_LABELS: Record<Language, Record<Difficulty, string>> = {
  es: {
    [Difficulty.BASIC]: 'Básico',
    [Difficulty.INTERMEDIATE_1]: 'Intermedio 1',
    [Difficulty.INTERMEDIATE_2]: 'Intermedio 2',
    [Difficulty.ADVANCED]: 'Avanzado',
    [Difficulty.EXPERT]: 'Experto / Muy difícil',
  },
  en: {
    [Difficulty.BASIC]: 'Basic',
    [Difficulty.INTERMEDIATE_1]: 'Intermediate 1',
    [Difficulty.INTERMEDIATE_2]: 'Intermediate 2',
    [Difficulty.ADVANCED]: 'Advanced',
    [Difficulty.EXPERT]: 'Expert / Very hard',
  },
};

const LEVEL_SPECS: Record<number, { name: string, visual: VisualComponentType, description: string, label: string, prop: string }> = {
  1: { name: "Button", visual: "M3_BUTTON", description: "Basic CTA button", label: "Enviar Formulario", prop: "buttonText" },
  2: { name: "Checkbox", visual: "M3_CHECKBOX", description: "Single or group checkbox", label: "Acepto los términos y condiciones", prop: "items" },
  3: { name: "Carousel", visual: "NONE", description: "Product carousel with multiple visible items", label: "Productos relacionados", prop: "ariaLabel" },
  4: { name: "Link", visual: "NONE", description: "Text link and icon link", label: "Ir a la página de ayuda", prop: "label" },
  5: { name: "Text Field", visual: "M3_TEXT_FIELD", description: "Material design input", label: "Correo electrónico", prop: "label" },
  6: { name: "List", visual: "NONE", description: "Unordered/ordered with semantic structure", label: "Lista de tareas", prop: "label" },
  7: { name: "Radio Button", visual: "M3_RADIO_GROUP", description: "Group with exclusive selection", label: "Opción Premium", prop: "items" },
  8: { name: "Tabs", visual: "NONE", description: "Navigation with panel switching", label: "Mi Perfil", prop: "label" },
  9: { name: "Loader", visual: "NONE", description: "Spinner with live region announcements", label: "Cargando contenido", prop: "ariaLabel" },
  10: { name: "Switch", visual: "M3_SWITCH", description: "Toggle with on/off states", label: "Activar modo oscuro", prop: "label" }
};

// ============================================================================
// HELPERS
// ============================================================================
const cleanAndParseJSON = <T>(text: string, context: string = 'AI response'): T => {
  try {
    let cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    cleaned = cleaned
      .replace(/[\n\r\t]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\\(?!["\\/bfnrt])/g, '\\\\');
    
    return JSON.parse(cleaned) as T;
  } catch (e) {
    console.error(`JSON Parse Error in ${context}:`, e);
    try {
      const lastBrace = text.lastIndexOf('}');
      if (lastBrace > 0) {
        const truncated = text.substring(0, lastBrace + 1);
        return JSON.parse(truncated) as T;
      }
    } catch (salvageError) {}
    throw new Error(`Failed to parse ${context}.`);
  }
};

const sanitizeJsonText = (raw: string): string => {
  if (!raw) return '';
  let text = raw
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .replace(/\u201c|\u201d/g, '"')
    .replace(/\u2018|\u2019/g, "'")
    .replace(/\u00a0/g, ' ')
    .trim();

  // Keep only JSON object/array body when model adds prose around it.
  const firstObject = text.indexOf('{');
  const firstArray = text.indexOf('[');
  const firstOpen = [firstObject, firstArray].filter((v) => v >= 0).sort((a, b) => a - b)[0];
  if (typeof firstOpen === 'number') {
    text = text.slice(firstOpen);
  }

  const lastObject = text.lastIndexOf('}');
  const lastArray = text.lastIndexOf(']');
  const lastClose = Math.max(lastObject, lastArray);
  if (lastClose >= 0) {
    text = text.slice(0, lastClose + 1);
  }

  // Remove trailing commas before } or ]
  text = text.replace(/,\s*([}\]])/g, '$1');
  return text;
};

const safeParseJson = <T>(raw: string): T | null => {
  const sanitized = sanitizeJsonText(raw);
  if (!sanitized) return null;
  try {
    return JSON.parse(sanitized) as T;
  } catch {
    // Try to salvage the first complete JSON object.
    const match = sanitized.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      const recovered = match[0].replace(/,\s*([}\]])/g, '$1');
      return JSON.parse(recovered) as T;
    } catch {
      return null;
    }
  }
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
};

const normalizeCategories = (categories: any) => ({
  generalSpecific: normalizeStringArray(categories?.generalSpecific),
  behavior: normalizeStringArray(categories?.behavior),
  alt: normalizeStringArray(categories?.alt),
  grouped: normalizeStringArray(categories?.grouped),
});

const ensureComponentDetails = (component: any, index: number) => ({
  componentTitle:
    String(component?.componentTitle ?? component?.name ?? `Component ${index + 1}`).trim()
    || `Component ${index + 1}`,
  box_2d: Array.isArray(component?.box_2d)
    ? component.box_2d.filter((n: unknown) => Number.isFinite(Number(n))).map(Number)
    : undefined,
  categories: normalizeCategories(component?.categories ?? {}),
});

const ensureSectionDetails = (section: any, index: number) => {
  const sectionCategories = normalizeCategories(section?.categories ?? {});
  const components = Array.isArray(section?.components)
    ? section.components.map((component: any, componentIndex: number) => ensureComponentDetails(component, componentIndex))
    : [];

  const hasComponentContent = components.some((component) =>
    component.categories.generalSpecific.length
    || component.categories.behavior.length
    || component.categories.alt.length
    || component.categories.grouped.length,
  );

  const normalizedComponents = hasComponentContent
    ? components
    : [
        {
          componentTitle: String(section?.regionTitle ?? `Component ${index + 1}`).trim() || `Component ${index + 1}`,
          categories: sectionCategories,
        },
      ];

  return {
    regionTitle: String(section?.regionTitle ?? `Section ${index + 1}`).trim() || `Section ${index + 1}`,
    box_2d: Array.isArray(section?.box_2d) ? section.box_2d.filter((n: unknown) => Number.isFinite(Number(n))).map(Number) : undefined,
    categories: sectionCategories,
    components: normalizedComponents,
  };
};

const sortSectionsByPosition = (sections: CheckerResult['complexSections']) => {
  return [...sections].sort((a, b) => {
    const [ax = Number.MAX_SAFE_INTEGER, ay = Number.MAX_SAFE_INTEGER] = a.box_2d ?? [];
    const [bx = Number.MAX_SAFE_INTEGER, by = Number.MAX_SAFE_INTEGER] = b.box_2d ?? [];
    if (ay !== by) return ay - by;
    return ax - bx;
  });
};

const dedupeSections = (sections: CheckerResult['complexSections']) => {
  const seen = new Set<string>();
  return sections.filter((section) => {
    const coords = section.box_2d?.join(',') ?? 'na';
    const key = `${section.regionTitle.toLowerCase()}|${coords}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeRegionTitles = (sections: CheckerResult['complexSections'], language: Language) => {
  const regionPrefix = language === 'es' ? 'Región' : 'Region';
  const untitled = language === 'es' ? 'Sin nombre' : 'Untitled';
  return sections.map((section, index) => {
    const rawTitle = (section.regionTitle ?? '').trim();
    const cleanedTitle = rawTitle.replace(/^(región|region)\s*\d+\s*[-–:]\s*/i, '').trim();
    return {
      ...section,
      regionTitle: `${regionPrefix} ${index + 1} - ${cleanedTitle || untitled}`,
    };
  });
};

const normalizeFocusOrder = (focusOrder: string[]) => {
  const seen = new Set<string>();
  const cleaned: string[] = [];
  focusOrder.forEach((entry) => {
    const value = entry.trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    cleaned.push(value);
  });
  return cleaned;
};

const normalizeLogoAsHomeButton = (sections: CheckerResult['complexSections']) => {
  return sections.map((section) => {
    const title = section.regionTitle.toLowerCase();
    if (!title.includes('logo')) return section;
    const hasHomeHint = section.categories.behavior.some((item) => /home|inicio/i.test(item));
    if (hasHomeHint) return section;
    return {
      ...section,
      categories: {
        ...section.categories,
        behavior: [...section.categories.behavior, 'Logo should behave as Home button and provide an accessible name.'],
      },
    };
  });
};

const inferLandmarksForSections = (sections: CheckerResult['complexSections']) => {
  return sections.map((section) => {
    const inferred = [...section.categories.generalSpecific];
    const title = section.regionTitle.toLowerCase();
    const hasLandmarkHint = inferred.some((item) => /landmark|header|main|footer|nav/i.test(item));
    if (!hasLandmarkHint) {
      if (/header|top bar|app bar/.test(title)) inferred.push('Landmark: header/banner');
      else if (/footer/.test(title)) inferred.push('Landmark: footer/contentinfo');
      else if (/menu|nav|navigation/.test(title)) inferred.push('Landmark: navigation');
      else inferred.push('Landmark: main/section');
    }
    return {
      ...section,
      categories: {
        ...section.categories,
        generalSpecific: inferred,
      },
    };
  });
};

const inferStructureFromSections = (sections: CheckerResult['complexSections']) => {
  const hints = sections.flatMap((section) => section.categories.generalSpecific);
  const unique = new Set<string>();
  hints.forEach((hint) => {
    if (/landmark|header|main|footer|navigation|banner|contentinfo/i.test(hint)) {
      unique.add(hint);
    }
  });
  return Array.from(unique);
};

const isSparseImageOnlyScreenDetection = (result: CheckerResult) => {
  const sections = result.complexSections ?? [];
  if (sections.length === 0) return true;
  if (sections.length > 1) return false;
  const single = sections[0];
  if ((single.components?.length ?? 0) > 1) return false;
  const genericTitle = /image|other|imagen|captura|screen|pantalla|ui/.test(single.regionTitle.toLowerCase());
  const componentItems = (single.components ?? []).reduce(
    (count, component) =>
      count
      + component.categories.generalSpecific.length
      + component.categories.behavior.length
      + component.categories.alt.length
      + component.categories.grouped.length,
    0,
  );
  const totalItems =
    single.categories.generalSpecific.length +
    single.categories.behavior.length +
    single.categories.alt.length +
    single.categories.grouped.length +
    componentItems;
  return genericTitle && totalItems <= 2;
};

const mergeWithFallback = (primary: CheckerResult, fallback: CheckerResult): CheckerResult => {
  const merged: CheckerResult = {
    ...fallback,
    ...primary,
    screenContext: {
      ...(fallback.screenContext ?? {}),
      ...(primary.screenContext ?? {}),
    },
    globalAnnotations: {
      viewTitle: primary.globalAnnotations?.viewTitle?.length ? primary.globalAnnotations.viewTitle : fallback.globalAnnotations.viewTitle,
      structure: primary.globalAnnotations?.structure?.length ? primary.globalAnnotations.structure : fallback.globalAnnotations.structure,
      heading: primary.globalAnnotations?.heading?.length ? primary.globalAnnotations.heading : fallback.globalAnnotations.heading,
      focusOrder: primary.globalAnnotations?.focusOrder?.length ? primary.globalAnnotations.focusOrder : fallback.globalAnnotations.focusOrder,
    },
    complexSections: primary.complexSections?.length ? primary.complexSections : fallback.complexSections,
  };
  return merged;
};

const buildFallbackDocumentation = (language: Language, userContext?: string): CheckerResult => {
  const isEs = language === 'es';
  return {
    isValidUI: true,
    layoutType: 'simple',
    screenContext: {
      device: isEs ? 'Desconocido' : 'Unknown',
      screenType: isEs ? 'Interfaz digital' : 'Digital interface',
      sector: isEs ? 'General' : 'General',
      description: userContext || (isEs ? 'Análisis generado con fallback.' : 'Analysis generated with fallback.'),
      language: LANGUAGE_NAMES[language],
      componentCount: 1,
    },
    globalAnnotations: {
      viewTitle: [isEs ? 'Verifica que el título describa la pantalla de forma única.' : 'Ensure the title uniquely describes the screen.'],
      structure: [isEs ? 'Incluye landmarks semánticos (header, main, footer).' : 'Include semantic landmarks (header, main, footer).'],
      heading: [isEs ? 'Mantén una jerarquía de encabezados sin saltos.' : 'Keep a heading hierarchy without skipped levels.'],
      focusOrder: [isEs ? 'Orden de foco visual: arriba-izquierda a abajo-derecha.' : 'Focus order should follow visual reading order.'],
    },
    complexSections: [
      {
        regionTitle: isEs ? 'Región 1 - Interfaz principal' : 'Region 1 - Main interface',
        categories: {
          generalSpecific: [isEs ? 'Revisar semántica y roles accesibles.' : 'Review semantic roles and accessible names.'],
          behavior: [isEs ? 'Validar estados de foco y navegación por teclado.' : 'Validate focus states and keyboard navigation.'],
          alt: [isEs ? 'Añadir texto alternativo en imágenes significativas.' : 'Add alt text for meaningful images.'],
          grouped: [isEs ? 'Agrupar controles relacionados con etiquetas claras.' : 'Group related controls with clear labels.'],
        },
        components: [
          {
            componentTitle: isEs ? 'Componente principal' : 'Main component',
            categories: {
              generalSpecific: [isEs ? 'Revisar semántica y roles accesibles.' : 'Review semantic roles and accessible names.'],
              behavior: [isEs ? 'Validar estados de foco y navegación por teclado.' : 'Validate focus states and keyboard navigation.'],
              alt: [isEs ? 'Añadir texto alternativo en imágenes significativas.' : 'Add alt text for meaningful images.'],
              grouped: [isEs ? 'Agrupar controles relacionados con etiquetas claras.' : 'Group related controls with clear labels.'],
            },
          },
        ],
      },
    ],
  };
};

const buildImageOnlyScreenHardRulesBlock = () => [
  'MAX PRIORITY (apply before any other rule):',
  '- This input is a rasterized screenshot.',
  '- Ignore layer structure for component detection.',
  '- Detect and document components from image pixels only.',
  '- Do not return a single generic Image/Other section if multiple controls are visible.',
  '- If label is uncertain, use a short functional name but keep component type.',
].join('\n');

const buildPromptBaseExtractionRules = () => [
  'Base extraction rules:',
  '- Use image as primary source.',
  '- Use hints only to validate labels/headings.',
  '- Detect major UI regions before listing details.',
].join('\n');

const buildPromptClassificationRules = () => [
  'Classification rules:',
  '- Detect actionable controls (buttons, links, icon buttons, tabs, toggles, inputs).',
  '- Distinguish static visual blocks from interactive controls.',
  '- Prefer functional component names over generic labels.',
  '- Inside each detected region, create one component entry per distinct control/component.',
  '- If a region has two similar controls (for example, two toggles), return two independent component entries with specific names.',
  '- Do not merge multiple controls into a single component entry.',
].join('\n');

const buildPromptRegionNamingRules = (language: Language) => {
  if (language === 'es') {
    return [
      'Region naming rules:',
      '- Every complexSections[].regionTitle must follow: "Región N - Nombre".',
      '- Use sequential N according to visual reading order (top to bottom, left to right).',
    ].join('\n');
  }
  return [
    'Region naming rules:',
    '- Every complexSections[].regionTitle must follow: "Region N - Name".',
    '- Use sequential N according to visual reading order (top to bottom, left to right).',
  ].join('\n');
};

const buildPromptLandmarkHeuristics = () => [
  'Landmark heuristics:',
  '- Identify header/banner, navigation, main content, complementary regions, and footer.',
  '- If unsure, propose the most likely landmark based on layout position.',
].join('\n');

const buildPromptHeadingHeuristics = () => [
  'Heading heuristics:',
  '- Infer heading hierarchy from visual prominence and placement.',
  '- Flag likely skipped heading levels.',
].join('\n');

const buildPromptSelectionHeuristics = (selectedComponent?: string) => {
  if (!selectedComponent) return '';
  return [
    `Component filter target: ${selectedComponent}`,
    '- Keep global analysis complete.',
    '- Prioritize detailed section output around the selected component when present.',
  ].join('\n');
};

const buildPromptBehaviorHeuristics = () => [
  'Behavior heuristics:',
  '- Validate keyboard navigation and focus order.',
  '- Flag icon-only controls that need accessible names.',
  '- Mention expected screen reader behavior when relevant.',
].join('\n');

const buildPromptCommerceHeuristics = () => [
  'Commerce/cards heuristics:',
  '- Detect card lists, pricing blocks, CTA hierarchy, and legal/footnote text proximity.',
  '- Flag ambiguous CTAs and missing context around price/legal terms.',
].join('\n');

const buildPromptAppSchemaRules = (device: 'mobile' | 'desktop' | 'component') => [
  `Output schema mode: ${device}`,
  '- Keep JSON keys in English.',
  '- Keep values in the target language.',
].join('\n');

const buildPromptGlobalOutputRules = () => [
  'Global output rules:',
  '- Return strictly valid JSON (no markdown, no prose).',
  '- Populate all required blocks, even if with concise defaults.',
  '- Keep findings specific and actionable.',
  '- Every region in complexSections must include at least one item in components[].',
].join('\n');

const buildPromptLanguageRules = (language: Language) => {
  const langName = LANGUAGE_NAMES[language];
  return [
    `Language rules:`,
    `- All string values must be in ${langName}.`,
    '- Do not translate JSON keys.',
  ].join('\n');
};

const buildPromptSchemaJsonBlock = () => [
  'JSON schema contract:',
  '- Root keys: isValidUI, layoutType, screenContext, globalAnnotations, complexSections.',
  '- globalAnnotations keys: viewTitle[], structure[], heading[], focusOrder[].',
  '- complexSections[] keys: regionTitle, box_2d?, categories{generalSpecific[], behavior[], alt[], grouped[]}, components[].',
  '- components[] keys: componentTitle, box_2d?, categories{generalSpecific[], behavior[], alt[], grouped[]}.',
].join('\n');

const buildComponentDocumentationHeuristics = (componentContextBlock?: string) => {
  if (!componentContextBlock) return '';
  return [
    'Component documentation mode:',
    '- Use provided component context as supporting metadata.',
    '- Do not let context override visual evidence.',
    componentContextBlock,
  ].join('\n');
};

const buildGeminiPrompt = (input: {
  device: 'mobile' | 'desktop' | 'component';
  frameName: string;
  platform: string;
  textHints?: string;
  customPrompt?: string;
  frameContentMode: 'structured' | 'image-only-screen';
  language: Language;
  selectedComponent?: string;
  componentContextBlock?: string;
  forceVisualExtraction?: boolean;
}) => {
  const blocks = [
    input.forceVisualExtraction ? buildImageOnlyScreenHardRulesBlock() : '',
    input.frameContentMode === 'image-only-screen' ? buildImageOnlyScreenHardRulesBlock() : '',
    `Frame: ${input.frameName}`,
    `Platform: ${input.platform}`,
    buildPromptBaseExtractionRules(),
    buildPromptClassificationRules(),
    buildPromptRegionNamingRules(input.language),
    buildPromptLandmarkHeuristics(),
    buildPromptHeadingHeuristics(),
    buildPromptSelectionHeuristics(input.selectedComponent),
    buildPromptBehaviorHeuristics(),
    buildPromptCommerceHeuristics(),
    buildPromptAppSchemaRules(input.device),
    buildPromptGlobalOutputRules(),
    buildPromptLanguageRules(input.language),
    buildPromptSchemaJsonBlock(),
    buildComponentDocumentationHeuristics(input.componentContextBlock),
    input.textHints ? `Text hints (supporting only):\n${input.textHints}` : '',
    input.customPrompt ? `Custom context:\n${input.customPrompt}` : '',
  ].filter(Boolean);
  return blocks.join('\n\n');
};

const extractRawTextFromGeminiResponse = (payload: any): string => {
  if (!payload) return '';
  if (typeof payload.output_text === 'string' && payload.output_text.trim()) return payload.output_text;
  const candidateText = payload.candidates?.[0]?.content?.parts?.find((part: any) => typeof part?.text === 'string')?.text;
  if (typeof candidateText === 'string' && candidateText.trim()) return candidateText;
  const openAiStyle = payload.choices?.[0]?.message?.content;
  if (typeof openAiStyle === 'string' && openAiStyle.trim()) return openAiStyle;
  return '';
};

const detectImageMimeType = (base64Image: string) => {
  if (base64Image.startsWith('/9j/')) return 'image/jpeg';
  if (base64Image.startsWith('iVBOR')) return 'image/png';
  if (base64Image.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
};

const callGeminiWithProxy = async (input: {
  prompt: string;
  base64Image: string;
  language: Language;
}) => {
  const endpoint = (import.meta as any).env?.VITE_SUPABASE_GEMINI_PROXY_ENDPOINT as string | undefined;
  const proxyModel = ((import.meta as any).env?.VITE_GEMINI_MODEL as string | undefined) ?? modelName;
  const proxyApiKey = ((import.meta as any).env?.VITE_GEMINI_PROXY_API_KEY as string | undefined) ?? '';
  const mimeType = detectImageMimeType(input.base64Image);

  if (endpoint) {
    const query = `model=${encodeURIComponent(proxyModel)}${proxyApiKey ? `&api_key=${encodeURIComponent(proxyApiKey)}` : ''}`;
    const url = `${endpoint}?${query}`;
    const requestBody = {
      contents: [{
        parts: [
          { text: input.prompt },
          {
            inlineData: {
              mimeType,
              data: input.base64Image,
            },
          },
        ],
      }],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 8192,
        responseMimeType: 'application/json',
      },
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Gemini proxy error (${response.status}): ${errorBody}`);
    }
    const payload = await response.json();
    return extractRawTextFromGeminiResponse(payload);
  }

  // Fallback to SDK when proxy endpoint is not configured.
  const { ai } = await getGenAiClient();
  const response = await ai.models.generateContent({
    model: modelName,
    contents: {
      parts: [
        { text: input.prompt },
        { inlineData: { mimeType, data: input.base64Image } },
      ],
    },
    config: {
      systemInstruction: getSystemInstruction(input.language),
      responseMimeType: 'application/json',
      maxOutputTokens: 8192,
      temperature: 0.3,
    },
  });

  return response.text || '';
};

const normalizeCheckerResult = (raw: any, language: Language, userContext?: string): CheckerResult => {
  const fallback = buildFallbackDocumentation(language, userContext);
  if (!raw || typeof raw !== 'object') return fallback;

  let sections = Array.isArray(raw.complexSections)
    ? raw.complexSections.map((section: any, index: number) => ensureSectionDetails(section, index))
    : [];
  sections = inferLandmarksForSections(sections);
  sections = normalizeLogoAsHomeButton(sections);
  sections = sortSectionsByPosition(sections);
  sections = dedupeSections(sections);
  sections = normalizeRegionTitles(sections, language);

  const normalized: CheckerResult = {
    isValidUI: Boolean(raw.isValidUI ?? true),
    layoutType: raw.layoutType === 'simple' || raw.layoutType === 'complex'
      ? raw.layoutType
      : (sections.length > 1 ? 'complex' : 'simple'),
    screenContext: {
      device: String(raw.screenContext?.device ?? fallback.screenContext?.device ?? ''),
      screenType: String(raw.screenContext?.screenType ?? fallback.screenContext?.screenType ?? ''),
      sector: String(raw.screenContext?.sector ?? fallback.screenContext?.sector ?? ''),
      description: String(raw.screenContext?.description ?? userContext ?? fallback.screenContext?.description ?? ''),
      language: String(raw.screenContext?.language ?? LANGUAGE_NAMES[language]),
      componentCount: Number(raw.screenContext?.componentCount ?? sections.length),
    },
    globalAnnotations: {
      viewTitle: normalizeStringArray(raw.globalAnnotations?.viewTitle),
      structure: normalizeStringArray(raw.globalAnnotations?.structure),
      heading: normalizeStringArray(raw.globalAnnotations?.heading),
      focusOrder: normalizeFocusOrder(normalizeStringArray(raw.globalAnnotations?.focusOrder)),
    },
    complexSections: sections,
  };

  if (!normalized.globalAnnotations.structure.length) {
    normalized.globalAnnotations.structure = inferStructureFromSections(normalized.complexSections);
  }

  return mergeWithFallback(normalized, fallback);
};

type AnalyzeImageOptions = {
  device?: 'mobile' | 'desktop' | 'component';
  frameName?: string;
  platform?: string;
  customPrompt?: string;
  textHints?: string;
  componentContextBlock?: string;
  frameContentMode?: 'structured' | 'image-only-screen';
  forceVisualExtraction?: boolean;
  selectedComponent?: string;
};

const getLanguageName = (language: Language): string => LANGUAGE_NAMES[language];

const getSystemInstruction = (language: Language): string => {
  const lang = getLanguageName(language);
  return `You are an expert Accessibility Auditor (WCAG 2.1 AA+). Output strictly in ${lang}. Your mission is to train designers/developers with PERFECT CONSISTENCY between visual examples, code, and screen reader output.`;
};

// ============================================================================
// PROMPT BUILDER
// ============================================================================
const buildLessonPrompt = (level: number, language: Language, context?: AdaptiveContext): string => {
  const spec = LEVEL_SPECS[level] || LEVEL_SPECS[1];
  const langName = getLanguageName(language);
  
  // Specific instructions per level
  let specificInstructions = "";
  if (level === 3) {
     specificInstructions = `
**SPECIAL CASE: CAROUSEL (Level 3)**
- Carousel Type: Product carousel with multiple visible items (3 visible, 6 total).
- Key Features:
  1. Use 'inert' attribute on hidden products (items 4-6).
  2. Implement navigation buttons (Previous/Next) with proper ARIA labels.
  3. Add live region for announcing navigation changes.
  4. Focus management: when reaching end, move focus to opposite arrow.
  5. Visible range indicator: "Mostrando productos 1 a 3 de 6".
- ARIA Structure:
  <section aria-roledescription="carrusel" aria-label="${spec.label}">
    <div role="group" aria-roledescription="slide" aria-label="Productos 1 a 3 de 6">...</div>
    <div inert>...</div>
    <div aria-live="polite" class="sr-only"></div>
  </section>
`;
  }

  // Construct prop specific prompt
  let propInstruction = "";
  if (spec.prop === 'items') {
      propInstruction = `Set 'visualComponentProps.items' to ["${spec.label}"].`;
  } else {
      propInstruction = `Set 'visualComponentProps.${spec.prop}' to "${spec.label}".`;
  }

  return `
CRITICAL MISSION: Generate a comprehensive accessibility lesson for Level ${level} (${spec.name}).
Language: ${langName}
Visual Component to generate: ${spec.visual} (${spec.description})
${context ? `User Context: Last Score ${context.lastScore}%, Trend ${context.trend}` : ''}

${specificInstructions}

**CRITICAL RULES (MUST FOLLOW):**
1. **NO 'dp' UNITS**: NEVER mention "dp". ALWAYS use "píxeles" or "px".
2. **MARKDOWN HEADERS**: In 'documentation.nonTechnical', use '###' for these exact sections:
   - ### ¿Qué es este componente?
   - ### Rol y Elemento Nativo
   - ### Nombre Accesible
   - ### Estados del Componente
   - ### Comportamiento y Contexto de Uso
3. **EXACT LABEL CONSISTENCY**: The label "${spec.label}" MUST be used identically in:
   - visualComponentProps
   - HTML code (<label> or aria-label)
   - Swift code (accessibilityLabel)
   - Android code (android:text or contentDescription)
   - Screen Reader outputs ("${spec.label}, ...")

STEP 1: VISUAL COMPONENT PROPS
Set 'visualComponent' to "${spec.visual}".
${propInstruction}
If the component is M3_BUTTON, set 'buttonText' to "${spec.label}".
If the component is M3_TEXT_FIELD, set 'label' to "${spec.label}".
If the component is M3_CHECKBOX or M3_RADIO_GROUP, set 'items' array containing "${spec.label}".
If the component is M3_SWITCH, set 'label' to "${spec.label}".

STEP 2: NON-TECHNICAL DOCUMENTATION (Markdown)
Write clear explanations using the ### headers defined above.
Explain the importance of the specific label "${spec.label}".

STEP 3: CODE EXAMPLES
Generate COMPLETE, PRODUCTION-READY code in 3 platforms.
**CRITICAL**: Use "${spec.label}" in the code.

<!-- HTML Example -->
<button>${spec.label}</button> or <label>${spec.label}</label>

<!-- iOS Example (Swift) -->
button.setTitle("${spec.label}", ...) or accessibilityLabel = "${spec.label}"

<!-- Android Example (XML) -->
android:text="${spec.label}" or android:contentDescription="${spec.label}"

STEP 4: SCREEN READER DOCUMENTATION (Markdown)

**Mobile (VoiceOver/TalkBack)**:
- Gesto: [Specific Gesture]
- Salida: "${spec.label}, [ROLE], [STATE]"

**Desktop (NVDA)**:
- Tecla: [Specific Key]
- Salida: "[ROLE], ${spec.label}, [STATE]"

STEP 5: TEST QUESTIONS
Generate EXACTLY 3 questions.

Output strict JSON matching the StudyLesson schema.
`;
};

// ============================================================================
// FUNCIONES EXPORTADAS
// ============================================================================

export const explainConceptSimply = async (
  phrase: string, 
  topic: string, 
  language: Language
): Promise<string> => {
  const { ai } = await getGenAiClient();
  const langName = getLanguageName(language);
  const prompt = `Context: Student learning "${topic}". Explain "${phrase}" in simple ${langName} (B2 level). Max 2 paragraphs. Avoid jargon.`;

  const response = await ai.models.generateContent({
    model: modelName,
    contents: prompt,
    config: { temperature: 0.7, maxOutputTokens: 200 }
  });

  return response.text || "No explanation generated.";
};

export const generateStudyLesson = async (
  level: number, 
  language: Language, 
  context?: AdaptiveContext
): Promise<StudyLesson> => {
  const { ai, Type } = await getGenAiClient();
  const prompt = buildLessonPrompt(level, language, context);

  const aiResponse = await ai.models.generateContent({
    model: modelName,
    contents: prompt,
    config: {
      systemInstruction: getSystemInstruction(language),
      responseMimeType: "application/json",
      maxOutputTokens: 8192,
      responseSchema: {
         type: Type.OBJECT,
         properties: {
            level: { type: Type.INTEGER },
            topicTag: { type: Type.STRING },
            introTitle: { type: Type.STRING },
            introSubtitle: { type: Type.STRING },
            learningPills: { 
              type: Type.ARRAY, 
              items: { 
                type: Type.OBJECT, 
                properties: { 
                  title: { type: Type.STRING }, 
                  description: { type: Type.STRING }, 
                  icon: { type: Type.STRING } 
                },
                required: ["title", "description", "icon"]
              } 
            },
            visualComponent: { 
              type: Type.STRING, 
              enum: ['M3_TEXT_FIELD', 'M3_BUTTON', 'M3_CARD', 'M3_SWITCH', 'M3_CHIPS', 'M3_CHECKBOX', 'M3_RADIO_GROUP', 'M3_FAB', 'NONE']
            },
            visualComponentProps: { 
                type: Type.OBJECT,
                properties: {
                    label: { type: Type.STRING },
                    placeholder: { type: Type.STRING },
                    helperText: { type: Type.STRING },
                    errorText: { type: Type.STRING },
                    buttonText: { type: Type.STRING },
                    title: { type: Type.STRING },
                    description: { type: Type.STRING },
                    ariaLabel: { type: Type.STRING },
                    role: { type: Type.STRING },
                    state: { type: Type.STRING },
                    items: { type: Type.ARRAY, items: { type: Type.STRING } },
                    secondaryText: { type: Type.STRING },
                    checked: { type: Type.BOOLEAN },
                    iconName: { type: Type.STRING }
                }
            },
            documentation: { 
                type: Type.OBJECT,
                properties: {
                    nonTechnical: { type: Type.STRING },
                    codeExamples: { 
                        type: Type.OBJECT, 
                        properties: { 
                            html: { type: Type.STRING }, 
                            ios: { type: Type.STRING }, 
                            android: { type: Type.STRING } 
                        },
                        required: ["html", "ios", "android"]
                    },
                    mobileScreenReader: { type: Type.STRING },
                    desktopScreenReader: { type: Type.STRING }
                },
                required: ["nonTechnical", "codeExamples", "mobileScreenReader", "desktopScreenReader"]
            },
            goodPractices: { type: Type.ARRAY, items: { type: Type.STRING } },
            badPractices: { type: Type.ARRAY, items: { type: Type.STRING } },
            externalResources: { 
                type: Type.ARRAY, 
                items: { 
                    type: Type.OBJECT,
                    properties: {
                        title: { type: Type.STRING },
                        url: { type: Type.STRING }
                    },
                    required: ["title", "url"]
                } 
            },
            test: { 
                type: Type.ARRAY, 
                items: { 
                    type: Type.OBJECT,
                    properties: {
                        question: { type: Type.STRING },
                        options: { type: Type.ARRAY, items: { type: Type.STRING } },
                        correctIndex: { type: Type.INTEGER },
                        successMessage: { type: Type.STRING },
                        failureMessage: { type: Type.STRING }
                    },
                    required: ["question", "options", "correctIndex", "successMessage", "failureMessage"]
                } 
            }
         },
         required: ["level", "topicTag", "introTitle", "introSubtitle", "learningPills", 
                   "visualComponent", "documentation", "goodPractices", "badPractices", 
                   "externalResources", "test"]
      }
    }
  });

  const parsedLesson = cleanAndParseJSON<StudyLesson>(aiResponse.text || "{}", 'Study Lesson');

  if (!parsedLesson.test || parsedLesson.test.length !== 3) {
    if (parsedLesson.test && parsedLesson.test.length > 3) {
      parsedLesson.test = parsedLesson.test.slice(0, 3);
    }
    while (!parsedLesson.test || parsedLesson.test.length < 3) {
      if (!parsedLesson.test) parsedLesson.test = [];
      parsedLesson.test.push({
        question: "Review Question",
        options: ["Option A", "Option B", "Option C", "Option D"],
        correctIndex: 0,
        successMessage: "Correct",
        failureMessage: "Review content"
      });
    }
  }

  return parsedLesson;
};

export const generateTest = async (
  difficulty: Difficulty, 
  type: TestType, 
  language: Language
): Promise<TestQuestion[]> => {
  const { ai, Type } = await getGenAiClient();
  const langName = getLanguageName(language);
  const difficultyLabel = DIFFICULTY_LABELS[language][difficulty];
  const topicLabel = TEST_TYPE_LABELS[language][type];
  const prompt = `10 ${difficultyLabel} accessibility questions about "${topicLabel}" (${langName}). Multiple choice, 4 options, 1 correct answer. Include a brief explanation for each answer.`;

  const response = await ai.models.generateContent({
    model: modelName,
    contents: prompt,
    config: {
      systemInstruction: getSystemInstruction(language),
      responseMimeType: "application/json",
      maxOutputTokens: 2500,
      responseSchema: { 
        type: Type.ARRAY, 
        items: { 
            type: Type.OBJECT,
            properties: {
                id: { type: Type.INTEGER },
                type: { type: Type.STRING },
                question: { type: Type.STRING },
                options: { type: Type.ARRAY, items: { type: Type.STRING } },
                correctAnswerIndex: { type: Type.INTEGER },
                explanation: { type: Type.STRING }
            },
            required: ["id", "type", "question", "options", "correctAnswerIndex", "explanation"]
        } 
      }
    }
  });
  
  return cleanAndParseJSON<TestQuestion[]>(response.text || "[]", 'Test Questions');
};

export const analyzeImage = async (
  base64Image: string, 
  language: Language, 
  userContext?: string,
  options: AnalyzeImageOptions = {}
): Promise<CheckerResult> => {
  const runSinglePass = async (forceVisualExtraction: boolean) => {
    const prompt = buildGeminiPrompt({
      device: options.device ?? 'desktop',
      frameName: options.frameName ?? 'Uploaded screenshot',
      platform: options.platform ?? 'web',
      customPrompt: [options.customPrompt, userContext].filter(Boolean).join('\n').trim() || undefined,
      textHints: options.textHints,
      componentContextBlock: options.componentContextBlock,
      frameContentMode: options.frameContentMode ?? 'image-only-screen',
      language,
      selectedComponent: options.selectedComponent,
      forceVisualExtraction,
    });

    const rawText = await callGeminiWithProxy({
      prompt,
      base64Image,
      language,
    });

    const parsed = safeParseJson<any>(rawText);
    if (!parsed) return null;
    return normalizeCheckerResult(parsed, language, userContext);
  };

  try {
    const firstPass = await runSinglePass(Boolean(options.forceVisualExtraction));
    if (!firstPass) {
      return buildFallbackDocumentation(language, userContext);
    }

    // For raster uploads, retry with stronger visual extraction when the first pass is too sparse.
    if ((options.frameContentMode ?? 'image-only-screen') === 'image-only-screen' && isSparseImageOnlyScreenDetection(firstPass)) {
      const retryPass = await runSinglePass(true);
      if (retryPass) {
        return retryPass;
      }
    }

    return firstPass;
  } catch (error) {
    console.error('Image Analysis pipeline failed:', error);
    return buildFallbackDocumentation(language, userContext);
  }
};

export const generateImage = async (prompt: string, size: '1K' | '2K' | '4K' = '1K'): Promise<string> => {
  const { ai } = await getGenAiClient();
  const response = await ai.models.generateContent({
    model: 'gemini-3-pro-image-preview',
    contents: { parts: [{ text: prompt }] },
    config: { imageConfig: { aspectRatio: "1:1", imageSize: size } },
  });
  for (const part of response.candidates[0].content.parts) {
    if (part.inlineData) return part.inlineData.data;
  }
  throw new Error("No image generated");
};

export const editImage = async (base64Image: string, prompt: string): Promise<string> => {
  const { ai } = await getGenAiClient();
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: { parts: [{ inlineData: { data: base64Image, mimeType: 'image/jpeg' } }, { text: prompt }] },
  });
  for (const part of response.candidates[0].content.parts) {
    if (part.inlineData) return part.inlineData.data;
  }
  throw new Error("No image edited");
};
